<script lang="ts">
	import { scale } from 'svelte/transition'
	import { cubicOut } from 'svelte/easing'
	import {
		MessageList,
		ChatInputBox,
		OverlayScrollbar,
		Button,
		ButtonType,
		Icon,
		ButtonSize,
		LoadingSpinner
	} from '@repo/commons/components'
	import type {
		ChatMessage,
		ChatStatus,
		DropdownItem,
		MessageActionType
	} from '@repo/commons/models'
	import { FeedbackActions, ChatConfirmModal, NewChatConfirmation, ChatHeader } from './index.ts'
	import { setupChatI18n, chatT, chatLocale } from '@repo/commons/i18n/chat'

	setupChatI18n()

	interface Props {
		headerColor?: string
		triggerColor?: string
		buttonColor?: string
		chatMessages: ChatMessage[]
		isChatVisible: boolean
		chatStatus?: ChatStatus
		isConfirmNewChatOpen?: boolean
		editingIds?: string[]
		chatTitle?: string
		visibleLangAction?: boolean
		visibleNewChatAction?: boolean
		visibleMinimizeAction?: boolean
		allowMessageEditing?: boolean
		showFeedbackButtons?: boolean
		showAttachButton?: boolean
		onCloseCallback: (step: 'CLOSE_WINDOW' | 'END_CHAT' | 'SHOW_FEEDBACK') => void
		onSetSelectedLanguage?: (item: DropdownItem) => Promise<void>
		onStartNewChatWithNewSession?: () => Promise<void>
		onSendMessage: (message: string) => Promise<void>
		onSetConfirmNewChatOpen?: (value: boolean) => void
		onOpenChat: () => Promise<void>
		onSubmitEditedMessage?: (message: ChatMessage) => void
		onCancelEditMode?: () => void
		onHandleMessageButtonClick?: ({
			buttonId,
			interactionId
		}: {
			buttonId: string
			interactionId: string
		}) => Promise<void>
		onSubmitFormMessage?: (formData: Record<string, any>) => Promise<void>
		onHandleActionCallback?: (action: MessageActionType, interactionId: string) => void
		onCustomComponentClicked?:
			| ((id: string, componentType: string, props: Record<string, any>) => void)
			| null
	}

	const {
		headerColor,
		triggerColor,
		buttonColor,
		chatMessages = [],
		isChatVisible,
		chatStatus,
		isConfirmNewChatOpen,
		editingIds,
		chatTitle = '',
		visibleLangAction = true,
		visibleNewChatAction = true,
		visibleMinimizeAction = true,
		allowMessageEditing = true,
		showFeedbackButtons = true,
		showAttachButton = true,
		onSetSelectedLanguage,
		onCloseCallback,
		onStartNewChatWithNewSession,
		onSendMessage,
		onSetConfirmNewChatOpen,
		onOpenChat,
		onSubmitEditedMessage,
		onCancelEditMode,
		onHandleMessageButtonClick,
		onSubmitFormMessage,
		onHandleActionCallback,
		onCustomComponentClicked = null
	}: Props = $props()

	let scrollContainerRef: OverlayScrollbar | null = $state(null)
	let selectedLanguage: DropdownItem | null = $state({
		id: 'DE',
		label: 'DE',
		icon: 'germanFlag'
	})

	function closeChatWithFeedback() {
		onCloseCallback('CLOSE_WINDOW') // isChatVisible ? 'SHOW_FEEDBACK' : 'CLOSE_WINDOW'
	}

	function handleFeedBack(feedback: 'negative' | 'positive', comment?: string) {
		onCloseCallback('END_CHAT')
	}

	async function onLanguageChange(item: DropdownItem) {
		await onSetSelectedLanguage?.(item)
		selectedLanguage = item
		chatLocale.set(item.id.toLocaleLowerCase())
	}

	export function scrollToBottom(behavior?: ScrollBehavior) {
		scrollContainerRef?.scrollToBottom(behavior)
	}
</script>

<div
	class="chatContainer"
	class:has-trigger-color={!!triggerColor}
	class:has-button-color={!!buttonColor}
	style:--chat-trigger-color={triggerColor}
	style:--chat-button-color={buttonColor}
>
	{#if isChatVisible}
		<div class="chatWindow" transition:scale={{ start: 0.9, duration: 200, easing: cubicOut }}>
			<ChatHeader
				{headerColor}
				{selectedLanguage}
				{chatTitle}
				{visibleLangAction}
				{visibleNewChatAction}
				{visibleMinimizeAction}
				onSelectLanguage={onLanguageChange}
				onNewChat={() => onSetConfirmNewChatOpen?.(true)}
				onFeedback={closeChatWithFeedback}
			/>

			<div class="chatBody">
				{#if chatStatus === 'loading'}
					<div class="loading">
						<LoadingSpinner size={28} color="rgba(0, 73, 128, 0.6)" />
						<span>{$chatT('Loading.Label')}</span>
					</div>
				{:else}
					<OverlayScrollbar bind:this={scrollContainerRef}>
						<MessageList
							messages={chatMessages}
							editingMessageIds={editingIds}
							showAvatar={false}
							{allowMessageEditing}
							{showFeedbackButtons}
							{onCustomComponentClicked}
							onSubmitEditing={onSubmitEditedMessage}
							onCancelEditing={onCancelEditMode}
							onButtonClick={onHandleMessageButtonClick}
							onFormSubmit={onSubmitFormMessage}
							onActionClick={onHandleActionCallback}
							onStartNewChat={onStartNewChatWithNewSession}
						/>
					</OverlayScrollbar>
				{/if}

				<div class="chatFooter">
					{#if chatStatus === 'opened'}
						<ChatInputBox {showAttachButton} onSend={onSendMessage} />
					{:else if chatStatus !== 'loading'}
						<FeedbackActions onHandleFeedback={handleFeedBack} />
					{/if}
				</div>

				<div class="author">
					{$chatT('PoweredBy.Label')} <a href="/">SemanticEdge</a>
				</div>
			</div>

			<ChatConfirmModal
				visible={isConfirmNewChatOpen}
				title={$chatT('NewChat.Modal.Title')}
				onclose={() => onSetConfirmNewChatOpen?.(false)}
			>
				<NewChatConfirmation
					onConfirm={onStartNewChatWithNewSession}
					onCancel={() => onSetConfirmNewChatOpen?.(false)}
				/>
			</ChatConfirmModal>
		</div>
	{/if}

	<Button
		buttonType={ButtonType.CUSTOM}
		buttonSize={ButtonSize.EXTRA_LARGE}
		extraProps={triggerColor
			? `background-color: color-mix(in srgb, ${triggerColor} 8%, white); border: 1px solid color-mix(in srgb, ${triggerColor} 20%, transparent); padding: 14px;`
			: 'background-color: #00498011; border: 1px solid #003A661A; padding: 14px;'}
		rounded
		onclick={onOpenChat}
		iconProps={{ name: 'expertise', size: 'lg', class: 'text-brand' }}
	/>
</div>

<style lang="postcss">
	.chatContainer {
		@apply fixed bottom-8 right-8 flex items-end justify-end gap-2 z-[99];
	}

	.chatWindow {
		@apply flex flex-col items-start shrink-0 overflow-hidden bg-white relative;
		@apply origin-bottom-right;
		@apply [height:clamp(400px,70vh,700px)] [max-height:calc(100vh-48px)] w-full max-w-[443px];
		@apply rounded-[24px];
		box-shadow:
			0px 8px 8px -4px rgba(0, 0, 0, 0.04),
			0px 20px 24px -4px rgba(0, 0, 0, 0.08);
	}

	.chatContainer.has-trigger-color > :global(.button-class svg) {
		color: var(--chat-trigger-color);
	}

	.chatContainer.has-button-color :global(.chatInputBox > .button-class:last-child svg) {
		color: var(--chat-button-color);
	}

	.chatContainer.has-button-color :global(.button-class.brand) {
		background-color: var(--chat-button-color);
	}

	.chatContainer.has-button-color :global(.button-class.brand:active) {
		filter: brightness(0.9);
	}

	.chatContainer.has-button-color :global(.actions .button-class:not(.custom)) {
		border-color: var(--chat-button-color);
	}

	.chatContainer.has-button-color :global(.actions .inverse-outline .button-text) {
		color: var(--chat-button-color);
	}

	.chatBody {
		@apply flex flex-col flex-1 min-h-0 w-full;
		@apply rounded-b-[24px] border border-t-0 border-[rgba(0,58,102,0.1)];
	}

	.loading {
		@apply flex flex-1 min-h-0 flex-col items-center justify-center gap-3 w-full;
		@apply text-[rgba(0,44,77,0.5)] text-[12px] leading-4;
	}

	.chatFooter {
		@apply flex w-full p-6 pb-[0.75rem] min-w-[370px];
	}

	.chatImage {
		@apply w-16 h-16 min-w-16 cursor-pointer object-cover object-[top_center];
		@apply rounded-[32px] border border-[rgba(0,58,102,0.1)];
	}

	.author {
		@apply flex items-center justify-center w-full gap-1 text-[rgba(0,44,77,0.5)] text-[12px] font-normal leading-4 mb-6;
	}

	.author a {
		@apply text-[rgba(0,29,51,0.7)] underline;
	}

	:global(.overlay-scrollbar) {
		@apply px-6;
	}

	:global(.modal .overlay-scrollbar) {
		@apply !p-0;
	}

	:global(.message-item) {
		@apply !px-4 !py-4;
	}

	.chatWindow :global(.overlay-scrollbar) {
		@apply flex-1 min-h-0 w-full;
	}

	.chatWindow :global(.message-item-wrapper) {
		@apply w-auto max-w-[85%];
	}
</style>
